<!-- 学生宠物管理：按部门查看学生宠物等级、状态并代领取/代更换。最后更新：2026-10-07 -->
<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="teaching" @tree-node-click="treeNodeClick" :type="0"></DepartmentPane>
    </section>
    <section class="dp-right student-pet-page">
      <div class="page-head">
        <p class="dp-dept-hint">
          <span class="dp-dept-hint__label">当前机构</span>
          <span class="dp-dept-hint__value">{{ departmentName || '未选择' }}</span>
        </p>
        <div class="filter-bar">
          <el-select v-model="query.semesterId" size="small" placeholder="学期" class="filter-bar__semester" @change="handleSemesterChange">
            <el-option v-for="s in semesters" :key="s.semesterId" :value="s.semesterId" :label="s.semesterName + (s.isCurrent ? '（当前）' : '')" />
          </el-select>
          <el-date-picker
            v-model="query.viewDate"
            type="date"
            size="small"
            value-format="yyyy-MM-dd"
            placeholder="查看日期"
            :clearable="false"
            :picker-options="datePickerOptions"
            class="filter-bar__date"
            @change="handleQuery" />
          <el-input
            v-model="query.keyword"
            size="small"
            clearable
            placeholder="姓名 / 学号"
            prefix-icon="el-icon-search"
            class="filter-bar__search"
            @keyup.enter.native="handleQuery"
            @clear="handleQuery" />
          <el-radio-group v-model="query.claimStatus" size="small" @change="handleQuery">
            <el-radio-button label="">全部</el-radio-button>
            <el-radio-button :label="1">已领取</el-radio-button>
            <el-radio-button :label="0">未领取</el-radio-button>
          </el-radio-group>
        </div>
      </div>
      <div class="filter-bar filter-bar--second">
        <el-select v-model="query.levelIndex" size="small" clearable placeholder="全部等级" class="filter-bar__select" @change="handleQuery">
          <el-option v-for="l in levels" :key="l.levelIndex" :value="l.levelIndex" :label="`Lv.${l.levelIndex} ${l.levelName}`" />
        </el-select>
        <el-select v-model="query.petId" size="small" clearable filterable placeholder="全部宠物" class="filter-bar__select" @change="handleQuery">
          <el-option v-for="p in pets" :key="p.petId" :value="p.petId" :label="p.petName + (p.status === 1 ? '' : '（已下架）')" />
        </el-select>
        <el-select v-model="query.moodCode" size="small" clearable :placeholder="isToday ? '今日状态' : '当日状态'" class="filter-bar__select" @change="handleQuery">
          <el-option :value="1" label="活力" />
          <el-option :value="0" label="平稳" />
          <el-option :value="2" label="调整" />
        </el-select>
        <el-button type="primary" size="small" icon="el-icon-search" :loading="loading" @click="handleQuery">查询</el-button>
        <el-button size="small" icon="el-icon-refresh-left" @click="resetFilters">重置</el-button>
        <span class="filter-bar__spacer"></span>
        <el-button size="small" type="warning" plain icon="el-icon-download" :loading="exporting" @click="exportList">导出</el-button>
        <el-button size="small" icon="el-icon-present" :disabled="!batchCandidates.length" @click="batchClaim">
          批量代领取<span v-if="batchCandidates.length">（{{ batchCandidates.length }}）</span>
        </el-button>
      </div>

      <div class="stat-strip">
        <div class="stat-card">
          <span class="stat-card__label">学生总数</span>
          <span class="stat-card__value">{{ stats.total }}</span>
        </div>
        <div class="stat-card stat-card--success">
          <span class="stat-card__label">已领取</span>
          <span class="stat-card__value">{{ stats.claimed }}</span>
        </div>
        <div class="stat-card stat-card--warning">
          <span class="stat-card__label">未领取</span>
          <span class="stat-card__value">{{ stats.unclaimed }}</span>
        </div>
        <div class="stat-card stat-card--rate">
          <span class="stat-card__label">领取率</span>
          <div class="rate-line">
            <span class="stat-card__value">{{ claimRate }}<small>%</small></span>
            <div class="rate-bar"><span :style="{ width: claimRate + '%' }"></span></div>
          </div>
        </div>
      </div>

      <div class="tbl-scroll-host">
        <el-table
          ref="table"
          :data="list"
          v-loading="loading"
          height="100%"
          style="width: 100%"
          class="pet-table"
          row-key="studentId"
          @selection-change="rows => (selection = rows)">
          <el-table-column type="selection" width="44" align="center" :selectable="row => !row.hasPet" />
          <el-table-column label="照片" width="70" align="center">
            <template slot-scope="{ row }">
              <oss-media-thumbs
                v-if="row.headPhotoImage"
                class="photo-thumb"
                :media="row.headPhotoImage"
                thumb-width="36px"
                thumb-height="36px" />
              <span v-else class="photo-empty">{{ (row.studentName || '?').slice(-1) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="姓名" min-width="110" align="center">
            <template slot-scope="{ row }">
              <el-tooltip placement="top" :open-delay="300" :content="(row.studentName || '') + '（点击查看成长详情）'" :disabled="!row.studentName">
                <span class="student-name is-link" @click="openDetail(row)">{{ row.studentName || '—' }}</span>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column label="学号" min-width="110" align="center" show-overflow-tooltip>
            <template slot-scope="{ row }"><span class="muted">{{ row.studentNo || '—' }}</span></template>
          </el-table-column>
          <el-table-column prop="departmentName" label="班级" min-width="110" align="center" show-overflow-tooltip />
          <el-table-column label="等级" min-width="130" align="center">
            <template slot-scope="{ row }">
              <span class="level-badge">Lv.{{ row.levelIndex || 1 }}</span>
              <span class="level-name">{{ row.levelName }}</span>
            </template>
          </el-table-column>
          <el-table-column label="学期分" width="90" align="center">
            <template slot-scope="{ row }"><span class="num">{{ formatScore(row.semesterScore) }}</span></template>
          </el-table-column>
          <el-table-column :label="isToday ? '今日状态' : '当日状态'" width="120" align="center">
            <template slot-scope="{ row }">
              <span :class="['mood-tag', 'mood-' + moodKey(row.mood)]">{{ row.moodText || '平稳' }}</span>
              <span :class="['today-score', scoreClass(row.todayScore)]">{{ signedScore(row.todayScore) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="宠物头像" width="80" align="center">
            <template slot-scope="{ row }">
              <oss-media-thumbs
                v-if="row.hasPet && row.avatarUrl"
                class="pet-thumb"
                :media="row.avatarUrl"
                thumb-width="36px"
                thumb-height="36px" />
              <span v-else-if="row.hasPet" class="pet-thumb-empty" title="该等级未上传头像"><i class="el-icon-picture-outline"></i></span>
              <span v-else class="muted">—</span>
            </template>
          </el-table-column>
          <el-table-column label="当前宠物" min-width="110" align="center">
            <template slot-scope="{ row }">
              <el-tooltip v-if="row.hasPet" placement="top" :open-delay="300" :content="row.petName || '已删除宠物'">
                <span class="pet-name">{{ row.petName || '已删除宠物' }}</span>
              </el-tooltip>
              <span v-else class="unclaimed-tag">未领取</span>
            </template>
          </el-table-column>
          <el-table-column label="家长更换" width="100" align="center">
            <template slot-scope="{ row }">
              <span class="num">{{ row.usedChangeCount }}</span>
              <span class="muted"> / {{ maxChange < 0 ? '不限' : maxChange }}</span>
            </template>
          </el-table-column>
          <el-table-column label="最近操作" width="140" align="center">
            <template slot-scope="{ row }"><span class="muted">{{ formatTime(row.lastChangeTime) }}</span></template>
          </el-table-column>
          <el-table-column label="操作" width="160" align="center" fixed="right">
            <template slot-scope="{ row }">
              <el-button size="mini" type="text" @click="openDetail(row)">详情</el-button>
              <el-button size="mini" :type="row.hasPet ? 'default' : 'primary'" :plain="!row.hasPet" @click="operate(row)">
                {{ row.hasPet ? '代更换' : '代领取' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="pagination-container">
        <el-pagination
          layout="total, sizes, prev, pager, next, jumper"
          :current-page="page.currentPage"
          :page-size="page.pageSize"
          :page-sizes="[20, 50, 100]"
          :total="page.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange" />
      </div>

      <!-- 选择宠物 -->
      <el-dialog :title="pickTitle" :visible.sync="pickVisible" width="720px" :close-on-click-modal="false">
        <div v-if="pickTarget && pickTarget.rows.length === 1 && pickTarget.rows[0].hasPet" class="pick-current">
          当前宠物：<b>{{ pickTarget.rows[0].petName }}</b>，管理员代更换不占用家长更换次数。
        </div>
        <div v-if="!availablePets.length" class="pick-empty">
          <i class="el-icon-warning-outline"></i>暂无上架宠物，请先到「宠物形象」中新增并上架。
        </div>
        <div v-else class="pick-grid">
          <div
            v-for="pet in availablePets"
            :key="pet.petId"
            :class="['pick-card', { 'is-active': pickPetId === pet.petId, 'is-disabled': isCurrentPet(pet) }]"
            @click="!isCurrentPet(pet) && (pickPetId = pet.petId)">
            <div class="pick-card__img">
              <img v-if="petCover(pet)" :src="resolveImageSrc(petCover(pet))" :alt="pet.petName" />
              <i v-else class="el-icon-picture-outline"></i>
            </div>
            <span class="pick-card__name">{{ pet.petName }}</span>
            <span v-if="isCurrentPet(pet)" class="pick-card__flag">当前</span>
            <i v-else-if="pickPetId === pet.petId" class="el-icon-success pick-card__check"></i>
          </div>
        </div>
        <span slot="footer">
          <el-button @click="pickVisible = false">取消</el-button>
          <el-button type="primary" :disabled="!pickPetId" :loading="submitting" @click="submitPick">确定</el-button>
        </span>
      </el-dialog>

      <student-pet-detail-drawer
        :visible.sync="detailVisible"
        :school-id="schoolId"
        :student-id="detailStudentId"
        :semester-id="query.semesterId"
        :semesters="semesters"
        :levels="levels" />
    </section>
  </section>
</template>

<script>
import DepartmentPane from '@/components/DepartmentPane.vue'
import StudentPetDetailDrawer from './components/StudentPetDetailDrawer.vue'
import { getPetConfig, getPetList, getPetStudentList, exportPetStudentList, getPetSemesterList, adminClaimPet, adminChangePet, batchClaimPet } from '@/api/modules/pet'
import { resolveImageSrc } from '@/utils/ossMediaUpload'
import { resolveActiveSchoolId, treeNodeDeptId, deptIdsEqual } from '@/utils/deptCascaderProps'

/** 日期格式化为 yyyy-MM-dd */
function formatDate (date) {
  const d = new Date(date)
  const pad = n => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

export default {
  name: 'StudentPetManage',
  components: { DepartmentPane, StudentPetDetailDrawer },
  data () {
    return {
      schoolId: 0,
      departmentId: 0,
      departmentName: '',
      loading: false,
      submitting: false,
      exporting: false,
      list: [],
      selection: [],
      query: { keyword: '', claimStatus: '', levelIndex: null, petId: null, moodCode: null, semesterId: null, viewDate: formatDate(new Date()) },
      levels: [],
      detailVisible: false,
      detailStudentId: 0,
      semesters: [],
      page: { currentPage: 1, pageSize: 20, total: 0 },
      stats: { total: 0, claimed: 0, unclaimed: 0 },
      maxChange: 2,
      pets: [],
      petsSchoolId: 0,
      pickVisible: false,
      pickTarget: null,
      pickPetId: 0
    }
  },
  computed: {
    isToday () {
      return this.query.viewDate === formatDate(new Date())
    },
    currentSemester () {
      return this.semesters.find(x => x.semesterId === this.query.semesterId) || null
    },
    /** 日期只能选所选学期范围内且不晚于今天 */
    datePickerOptions () {
      const semester = this.currentSemester
      const today = formatDate(new Date())
      return {
        disabledDate: date => {
          const value = formatDate(date)
          if (value > today) return true
          if (!semester) return false
          return value < String(semester.startDate || '').slice(0, 10) || value > String(semester.endDate || '').slice(0, 10)
        }
      }
    },
    claimRate () {
      return this.stats.total ? Math.round(this.stats.claimed / this.stats.total * 100) : 0
    },
    batchCandidates () {
      return this.selection.filter(x => !x.hasPet)
    },
    availablePets () {
      return this.pets.filter(x => x.status === 1)
    },
    pickTitle () {
      if (!this.pickTarget) return '选择宠物'
      const rows = this.pickTarget.rows
      if (rows.length > 1) return `批量代领取（${rows.length} 名学生）`
      return `${rows[0].hasPet ? '代更换' : '代领取'} · ${rows[0].studentName}`
    }
  },
  methods: {
    resolveImageSrc (url) {
      return resolveImageSrc(url)
    },
    /** 左侧部门树点击：学校取顶栏当前学校，部门含下级 */
    async treeNodeClick (data) {
      this.departmentId = treeNodeDeptId(data)
      this.departmentName = (data && (data.label || data.Name)) || ''
      const schoolId = resolveActiveSchoolId(this)
      if (schoolId && !deptIdsEqual(schoolId, this.schoolId)) {
        this.schoolId = schoolId
        await this.loadSchoolMeta()
      }
      this.handleQuery()
    },
    async loadSchoolMeta () {
      if (!this.schoolId) return
      const [configRes, petRes, semesterRes] = await Promise.all([
        getPetConfig({ schoolId: this.schoolId }),
        getPetList({ schoolId: this.schoolId }),
        getPetSemesterList({ schoolId: this.schoolId })
      ])
      const config = configRes.data && configRes.data.response
      this.maxChange = config ? config.maxChangePerSemester : 2
      this.levels = (config && config.levels) || []
      if (this.query.levelIndex && !this.levels.some(x => x.levelIndex === this.query.levelIndex)) this.query.levelIndex = null
      this.pets = (petRes.data && petRes.data.response) || []
      this.petsSchoolId = this.schoolId
      this.semesters = (semesterRes.data && semesterRes.data.response) || []
      const current = this.semesters.find(x => x.isCurrent) || this.semesters[0]
      this.query.semesterId = current ? current.semesterId : null
      this.clampViewDate()
    },
    /** 查看日期限制在学期范围内：超出则取学期结束日与今天中较早的一天 */
    clampViewDate () {
      const semester = this.currentSemester
      const today = formatDate(new Date())
      if (!semester) {
        this.query.viewDate = today
        return
      }
      const start = String(semester.startDate || '').slice(0, 10)
      const end = String(semester.endDate || '').slice(0, 10)
      const latest = end && end < today ? end : today
      const value = this.query.viewDate
      if (!value || value > latest || (start && value < start)) this.query.viewDate = latest
    },
    handleSemesterChange () {
      this.clampViewDate()
      this.handleQuery()
    },
    handleQuery () {
      this.page.currentPage = 1
      this.load()
    },
    /** 列表与导出共用的查询参数 */
    buildParams () {
      const optional = v => (v === '' || v === undefined ? null : v)
      return {
        schoolId: this.schoolId,
        departmentId: this.departmentId,
        keyword: (this.query.keyword || '').trim(),
        claimStatus: optional(this.query.claimStatus),
        levelIndex: optional(this.query.levelIndex),
        petId: optional(this.query.petId),
        moodCode: optional(this.query.moodCode),
        semesterId: this.query.semesterId || null,
        viewDate: this.query.viewDate || null
      }
    },
    resetFilters () {
      Object.assign(this.query, { keyword: '', claimStatus: '', levelIndex: null, petId: null, moodCode: null })
      this.handleQuery()
    },
    /** 按当前筛选导出全部学生（不分页） */
    async exportList () {
      if (!this.schoolId || !this.departmentId) return this.$message.warning('请先在左侧选择部门')
      this.exporting = true
      try {
        const res = await exportPetStudentList(this.buildParams())
        const blob = res.data
        if (!blob || !blob.size) return this.$message.error('导出数据为空')
        const url = window.URL.createObjectURL(new Blob([blob]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `学生宠物_${this.departmentName || ''}_${this.query.viewDate || ''}.xlsx`)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (err) {
        const data = err && err.response && err.response.data
        const text = data && typeof data.text === 'function' ? await data.text() : ''
        this.$message.error(text || '导出失败')
      } finally {
        this.exporting = false
      }
    },
    openDetail (row) {
      this.detailStudentId = row.studentId
      this.detailVisible = true
    },
    async load () {
      if (!this.schoolId || !this.departmentId) return
      this.loading = true
      try {
        const res = await getPetStudentList({
          ...this.buildParams(),
          page: this.page.currentPage,
          pageSize: this.page.pageSize
        })
        if (res.data && res.data.success) {
          const data = res.data.response || {}
          const rows = data.data || []
          await this.prefetchListOssMedia(rows, ['headPhotoImage', 'avatarUrl'])
          this.list = rows
          this.page.total = data.dataCount || 0
          this.stats = {
            total: data.totalStudentCount || 0,
            claimed: data.claimedCount || 0,
            unclaimed: data.unclaimedCount || 0
          }
        } else {
          this.list = []
          this.$message.error((res.data && res.data.msg) || '查询失败')
        }
      } finally {
        this.loading = false
      }
    },
    handleCurrentChange (val) {
      this.page.currentPage = val
      this.load()
    },
    handleSizeChange (val) {
      this.page.pageSize = val
      this.handleQuery()
    },
    async ensurePets () {
      if (this.petsSchoolId !== this.schoolId) await this.loadSchoolMeta()
    },
    async operate (row) {
      await this.ensurePets()
      this.pickTarget = { rows: [row] }
      this.pickPetId = 0
      this.pickVisible = true
    },
    async batchClaim () {
      if (!this.batchCandidates.length) return this.$message.warning('请勾选未领取宠物的学生')
      await this.ensurePets()
      this.pickTarget = { rows: this.batchCandidates.slice() }
      this.pickPetId = 0
      this.pickVisible = true
    },
    isCurrentPet (pet) {
      const rows = (this.pickTarget && this.pickTarget.rows) || []
      return rows.length === 1 && rows[0].hasPet && rows[0].petId === pet.petId
    },
    petCover (pet) {
      if (pet.coverUrl) return pet.coverUrl
      const stable = (pet.images || []).filter(x => x.mood === 0 && x.imageUrl).sort((a, b) => a.levelIndex - b.levelIndex)
      return stable.length ? stable[0].imageUrl : ''
    },
    async submitPick () {
      const rows = this.pickTarget.rows
      this.submitting = true
      try {
        if (rows.length > 1) {
          const res = await batchClaimPet(this.schoolId, { studentIds: rows.map(x => x.studentId), petId: this.pickPetId })
          if (!(res.data && res.data.success)) return this.$message.error((res.data && res.data.msg) || '操作失败')
          const successCount = (res.data.response || []).length
          const failCount = rows.length - successCount
          if (failCount > 0) this.$message.warning(`成功 ${successCount} 人，失败 ${failCount} 人`)
          else this.$message.success(`已为 ${successCount} 名学生领取宠物`)
        } else {
          const row = rows[0]
          const data = { studentId: row.studentId, petId: this.pickPetId }
          const res = row.hasPet ? await adminChangePet(this.schoolId, data) : await adminClaimPet(this.schoolId, data)
          if (!(res.data && res.data.success)) return this.$message.error((res.data && res.data.msg) || '操作失败')
          this.$message.success(row.hasPet ? '更换成功' : '领取成功')
        }
        this.pickVisible = false
        if (this.$refs.table) this.$refs.table.clearSelection()
        this.load()
      } finally {
        this.submitting = false
      }
    },
    moodKey (mood) {
      const value = String(mood || '').toLowerCase()
      if (value === 'energetic') return 'energetic'
      if (value === 'adjusting') return 'adjusting'
      return 'stable'
    },
    formatScore (value) {
      const num = Number(value) || 0
      return Number.isInteger(num) ? num : num.toFixed(1)
    },
    signedScore (value) {
      const num = Number(value) || 0
      return num > 0 ? `+${this.formatScore(num)}` : `${this.formatScore(num)}`
    },
    scoreClass (value) {
      const num = Number(value) || 0
      return num > 0 ? 'is-up' : num < 0 ? 'is-down' : ''
    },
    formatTime (value) {
      if (!value) return '—'
      return String(value).replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style scoped>
.student-pet-page { display: flex; flex-direction: column; min-height: 0; }
.page-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 10px; }
.page-head .dp-dept-hint { margin: 0; }
.filter-bar { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.filter-bar__search { width: 160px; }
.filter-bar__semester { width: 190px; }
.filter-bar__date { width: 140px !important; }
.filter-bar--second { margin-bottom: 10px; }
.filter-bar__select { width: 150px; }
.filter-bar__spacer { flex: 1; }
.student-name.is-link { cursor: pointer; color: #1d4ed8; }
.student-name.is-link:hover { text-decoration: underline; }

.stat-strip { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; margin-bottom: 12px; }
.stat-card { display: flex; flex-direction: column; gap: 4px; padding: 10px 14px; background: #fff; border: 1px solid #e2e8f0; border-left: 3px solid #2563eb; border-radius: 10px; }
.stat-card--success { border-left-color: #16a34a; }
.stat-card--warning { border-left-color: #f59e0b; }
.stat-card--rate { border-left-color: #7c3aed; }
.stat-card__label { font-size: 12px; color: #64748b; }
.stat-card__value { font-size: 20px; font-weight: 600; color: #0f172a; line-height: 1.2; font-variant-numeric: tabular-nums; }
.stat-card__value small { font-size: 12px; font-weight: 400; color: #64748b; margin-left: 1px; }
.rate-line { display: flex; align-items: center; gap: 12px; }
.rate-bar { flex: 1; height: 6px; border-radius: 999px; background: #f1f5f9; overflow: hidden; }
.rate-bar span { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #a78bfa, #7c3aed); transition: width .3s ease; }

.pet-table /deep/ .el-table__row:hover > td { background: #f5f9ff !important; }
.photo-thumb { display: inline-flex; justify-content: center; }
.photo-thumb /deep/ img, .pet-thumb /deep/ img { border-radius: 6px; object-fit: cover; }
.photo-empty { display: inline-flex; align-items: center; justify-content: center; width: 36px; height: 36px; border-radius: 6px; background: #eff6ff; border: 1px solid #bfdbfe; color: #1d4ed8; font-size: 13px; font-weight: 600; }
.student-name { display: inline-block; max-width: 100%; vertical-align: middle; cursor: default; font-weight: 500; color: #0f172a; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pet-thumb { display: inline-flex; justify-content: center; }
.pet-thumb /deep/ img { border-radius: 50%; }
.pet-thumb-empty { width: 36px; height: 36px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; background: #f8fafc; border: 1px dashed #cbd5e1; color: #94a3b8; }
.pet-name { display: inline-block; max-width: 100%; vertical-align: middle; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; cursor: default; }
.level-badge { display: inline-block; padding: 1px 7px; margin-right: 6px; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: 12px; font-weight: 600; }
.level-name { font-size: 12px; color: #475569; }
.num { font-variant-numeric: tabular-nums; color: #0f172a; font-weight: 500; }
.muted { color: #64748b; font-size: 12px; }
.mood-tag { display: inline-block; padding: 1px 8px; border-radius: 999px; font-size: 12px; font-weight: 500; }
.mood-stable { background: #eff6ff; color: #1d4ed8; }
.mood-energetic { background: #dcfce7; color: #15803d; }
.mood-adjusting { background: #fef3c7; color: #b45309; }
.today-score { margin-left: 6px; font-size: 12px; color: #64748b; font-variant-numeric: tabular-nums; }
.today-score.is-up { color: #15803d; }
.today-score.is-down { color: #b45309; }
.pet-name { color: #0f172a; font-weight: 500; }
.unclaimed-tag { display: inline-block; padding: 1px 8px; border-radius: 999px; background: #f1f5f9; color: #64748b; font-size: 12px; }

.pick-current { margin-bottom: 12px; padding: 8px 12px; border-radius: 8px; background: #f8fafc; color: #475569; font-size: 13px; }
.pick-empty { padding: 36px 0; text-align: center; color: #64748b; }
.pick-empty i { margin-right: 6px; color: #f59e0b; }
.pick-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 12px; max-height: min(460px, calc(88vh - 300px)); overflow-y: auto; padding: 2px; }
.pick-card { position: relative; display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 12px 10px; border: 1px solid #e2e8f0; border-radius: 10px; cursor: pointer; transition: border-color .2s ease, background-color .2s ease; }
.pick-card:hover { border-color: #93c5fd; }
.pick-card.is-active { border-color: #2563eb; background: #eff6ff; }
.pick-card.is-disabled { cursor: not-allowed; opacity: .55; }
.pick-card__img { width: 88px; height: 88px; display: flex; align-items: center; justify-content: center; border-radius: 8px; background: #f8fafc; overflow: hidden; color: #94a3b8; font-size: 28px; }
.pick-card__img img { width: 100%; height: 100%; object-fit: contain; }
.pick-card__name { font-size: 13px; font-weight: 500; color: #0f172a; max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pick-card__flag { position: absolute; top: 6px; right: 6px; padding: 0 6px; border-radius: 999px; background: #f1f5f9; color: #475569; font-size: 12px; }
.pick-card__check { position: absolute; top: 6px; right: 6px; color: #2563eb; font-size: 18px; }

@media (max-width: 1280px) {
  .stat-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
</style>
